import { Accessibility, ALargeSmall, Gauge, Keyboard, Layers3, Music2, Palette, SlidersHorizontal, Volume2 } from 'lucide-react'
import { PitchRail } from './PitchRail'
import { absolutePitchScales, scaleDisplayName } from '../domain/pitchContext'
import type { AbsolutePitchSource, AnswerInput, Difficulty, Instrument, PlaybackStyle, Preferences, TextSize, Theme } from '../domain/types'
import { captureEvent } from '../lib/observability'

type SettingsProps = {
  preferences: Preferences
  onChange: (preferences: Preferences) => void
  inLesson?: boolean
}

function OptionButton<T extends string>({ value, current, onSelect, children }: { value: T; current: T; onSelect: (value: T) => void; children: React.ReactNode }) {
  return <button aria-pressed={value === current} className={value === current ? 'segmented-option selected' : 'segmented-option'} type="button" onClick={() => onSelect(value)}>{children}</button>
}

export function Settings({ preferences, onChange, inLesson = false }: SettingsProps) {
  const update = <K extends keyof Preferences>(key: K, value: Preferences[K]) => {
    onChange({ ...preferences, [key]: value })
    captureEvent('setting_changed', { setting: key, value: String(value) })
  }

  return (
    <main id="main-content" className="page-shell narrow-page">
      <section className="page-hero compact">
        <div><h1>Options</h1></div>
        <p>{inLesson ? 'Changes apply to the current lesson. Your question and score are preserved.' : 'These settings apply to new rounds and are stored on this device.'}</p>
      </section>

      <section className="settings-stack">
        <div className="settings-group">
          <div className="settings-label"><Gauge aria-hidden="true" /><div><strong>Difficulty</strong><span>Controls the available musical vocabulary</span></div></div>
          <div className="segmented-control">
            {(['beginner', 'intermediate', 'advanced'] as Difficulty[]).map((value) => <OptionButton key={value} value={value} current={preferences.difficulty} onSelect={(choice) => update('difficulty', choice)}>{value}</OptionButton>)}
          </div>
        </div>

        <div className="settings-group pitch-context-settings">
          <div className="settings-label"><Music2 aria-hidden="true" /><div><strong>Absolute Pitch Context</strong><span>Choose chromatic randomness or practice inside a tonic and scale</span></div></div>

          <div className="context-setting-row">
            <div><strong>Show pitch rail</strong><span>Display the tonic selector on Absolute Pitch exercise panels</span></div>
            <div className="segmented-control compact-control">
              <OptionButton value="visible" current={preferences.showPitchRail ? 'visible' : 'hidden'} onSelect={() => update('showPitchRail', true)}>Visible</OptionButton>
              <OptionButton value="hidden" current={preferences.showPitchRail ? 'visible' : 'hidden'} onSelect={() => update('showPitchRail', false)}>Hidden</OptionButton>
            </div>
          </div>

          <div className="context-setting-row">
            <div><strong>Question source</strong><span>Use all 12 pitch classes or notes and chords from one scale</span></div>
            <div className="segmented-control compact-control">
              <OptionButton<AbsolutePitchSource> value="random" current={preferences.absolutePitchSource} onSelect={(choice) => update('absolutePitchSource', choice)}>Fully random</OptionButton>
              <OptionButton<AbsolutePitchSource> value="scale" current={preferences.absolutePitchSource} onSelect={(choice) => update('absolutePitchSource', choice)}>Within a scale</OptionButton>
            </div>
          </div>

          <div className="context-scale-grid">
            <label className="select-field">
              <span>Scale</span>
              <select value={preferences.absolutePitchScaleId} onChange={(event) => update('absolutePitchScaleId', event.target.value)}>
                {absolutePitchScales().map((scale) => <option value={scale.id} key={scale.id}>{scaleDisplayName(scale)}</option>)}
              </select>
            </label>
            <div className="context-tonic-copy"><strong>Tonic / key center</strong><span>Uses automatic, scale-appropriate accidental spelling</span></div>
          </div>
          <PitchRail value={preferences.absolutePitchTonic} onChange={(pitchClass) => update('absolutePitchTonic', pitchClass)} label="Settings tonic selector" />
        </div>

        <div className="settings-group">
          <div className="settings-label"><Volume2 aria-hidden="true" /><div><strong>Instrument tone</strong><span>Modeled locally with Web Audio—no sample download required</span></div></div>
          <div className="segmented-control vertical-mobile">
            <OptionButton<Instrument> value="studio-piano" current={preferences.instrument} onSelect={(choice) => update('instrument', choice)}>Studio piano</OptionButton>
            <OptionButton<Instrument> value="classic-synth" current={preferences.instrument} onSelect={(choice) => update('instrument', choice)}>Classic synth</OptionButton>
            <OptionButton<Instrument> value="tonewheel" current={preferences.instrument} onSelect={(choice) => update('instrument', choice)}>Tonewheel</OptionButton>
          </div>
        </div>

        <div className="settings-group split-setting">
          <div className="settings-label"><Music2 aria-hidden="true" /><div><strong>Chord playback</strong><span>Together or one note at a time</span></div></div>
          <div className="segmented-control compact-control">
            {(['harmonic', 'broken'] as PlaybackStyle[]).map((value) => <OptionButton key={value} value={value} current={preferences.playbackStyle} onSelect={(choice) => update('playbackStyle', choice)}>{value}</OptionButton>)}
          </div>
        </div>

        <div className="settings-group range-group">
          <div className="settings-label"><SlidersHorizontal aria-hidden="true" /><div><strong>Round length</strong><span>Questions in each round</span></div></div>
          <div className="range-control"><input type="range" min="5" max="20" step="5" value={preferences.roundLength} onChange={(event) => update('roundLength', Number(event.target.value))} aria-label="Round length" /><strong>{preferences.roundLength}</strong></div>
        </div>

        <div className="settings-group range-group">
          <div className="settings-label"><Layers3 aria-hidden="true" /><div><strong>Absolute-pitch range</strong><span>Octaves used in note and chord questions</span></div></div>
          <div className="range-control"><input type="range" min="1" max="4" value={preferences.octaveRange} onChange={(event) => update('octaveRange', Number(event.target.value))} aria-label="Octave range" /><strong>{preferences.octaveRange} oct.</strong></div>
        </div>

        <div className="settings-group split-setting">
          <div className="settings-label"><Keyboard aria-hidden="true" /><div><strong>Pitch-answer input</strong><span>Used for note and chord-root answers</span></div></div>
          <div className="segmented-control compact-control">
            <OptionButton<AnswerInput> value="labels" current={preferences.answerInput} onSelect={(choice) => update('answerInput', choice)}>Note labels</OptionButton>
            <OptionButton<AnswerInput> value="keyboard" current={preferences.answerInput} onSelect={(choice) => update('answerInput', choice)}>Piano keyboard</OptionButton>
          </div>
        </div>

        <div className="settings-group range-group">
          <div className="settings-label"><Accessibility aria-hidden="true" /><div><strong>Playback speed</strong><span>Spacing and duration for musical examples</span></div></div>
          <div className="range-control"><input type="range" min="0.7" max="1.5" step="0.1" value={preferences.playbackSpeed} onChange={(event) => update('playbackSpeed', Number(event.target.value))} aria-label="Playback speed" /><strong>{preferences.playbackSpeed.toFixed(1)}×</strong></div>
        </div>

        <div className="settings-group split-setting text-size-setting">
          <div className="settings-label"><ALargeSmall aria-hidden="true" /><div><strong>Text size</strong><span>Increase interface text for easier reading</span></div></div>
          <div className="segmented-control text-size-control" role="group" aria-label="Text size">
            <OptionButton<TextSize> value="standard" current={preferences.textSize} onSelect={(choice) => update('textSize', choice)}>Standard</OptionButton>
            <OptionButton<TextSize> value="large" current={preferences.textSize} onSelect={(choice) => update('textSize', choice)}>Large</OptionButton>
            <OptionButton<TextSize> value="extra-large" current={preferences.textSize} onSelect={(choice) => update('textSize', choice)}>Extra Large</OptionButton>
          </div>
        </div>

        <div className="settings-group theme-setting">
          <div className="settings-label"><Palette aria-hidden="true" /><div><strong>Visual theme</strong><span>Choose the interface color scheme</span></div></div>
          <div className="theme-grid">
            {([
              ['studio', 'Studio Dark', 'Dark blue'],
              ['paper', 'Paper Light', 'Light neutral'],
              ['contrast', 'Stage Contrast', 'High contrast'],
            ] as Array<[Theme, string, string]>).map(([value, label, description]) => (
              <button className={preferences.theme === value ? `theme-choice ${value} selected` : `theme-choice ${value}`} type="button" key={value} onClick={() => update('theme', value)}>
                <span className="theme-swatch"><i /><i /><i /></span><strong>{label}</strong><small>{description}</small>
              </button>
            ))}
          </div>
        </div>
      </section>

      <aside className="shortcut-note"><Keyboard aria-hidden="true" /><div><strong>Keyboard shortcuts</strong><p>Press Space to play, number keys to answer, and Enter for the next sound.</p></div></aside>
    </main>
  )
}
